"use client";

/**
 * `<SiteNav>` — THE unified site navigation (Phase 3, shell unification).
 *
 * Replaces both previous shells:
 *   - `components/layout/navbar.tsx` (`Navbar`) — old app/report shell
 *   - `components/marketing/nav.tsx` (`MarketingNav`) — marketing shell
 * Both modules now re-export this component so every page — logged-in
 * and logged-out — renders identical nav markup.
 *
 * Primary links are AUTH-AWARE (restored after the Phase 3 unification
 * accidentally dropped the logged-in app tabs):
 *   - Logged out → marketing set (per the landing sample / Phase 3 spec):
 *     Compass · Sample brief · Reports (/reports) · Methodology · Pricing
 *   - Logged in  → app tabs (the old Navbar set, plus Dashboard):
 *     Dashboard · Stocks · Funds · Compass · Simulate · Industry ·
 *     Glossary · Blog
 *
 * Auth-aware right side:
 *   - Logged out → "Log in" + "Start free"
 *   - Logged in  → account menu (Dashboard / My Reports / Browse Reports /
 *     Account / Admin / Sign Out) — the existing session + plan + admin
 *     logic from the old Navbar, carried over verbatim.
 *
 * A11y (carried over from the marketing nav + WU3 fixes):
 *   - mobile toggle has `aria-label` + `aria-expanded` + `aria-controls`
 *   - mobile menu is a `role="dialog"` overlay with Escape-to-close and
 *     body scroll-lock while open
 *   - account trigger has `aria-haspopup` + `aria-expanded`; dropdown
 *     uses menu semantics
 *
 * `topOffset` exists for marketing pages where the 32px `TickerBar`
 * is sticky at top-0 (z-60): the nav sticks just below it. App pages
 * use the default 0.
 *
 * The old "BETA · Survey" badge is gone from the nav — the survey link
 * was relocated to the SiteFooter Resources column.
 */

import Link from "next/link";
import Image from "next/image";
import { useSession, signOut } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/shared/logo";
import { Button } from "@/components/ui/button";

interface SiteNavProps {
  /**
   * Sticky `top` offset in px. Pass 32 on pages that render the
   * marketing `<TickerBar>` (fixed-height 32px, sticky top-0, z-60)
   * above the nav. Defaults to 0 for app/report pages.
   */
  topOffset?: number;
}

interface PrimaryLink {
  href: string;
  label: string;
  /** Returns true when this tab should highlight for the route. */
  isActive: (pathname: string) => boolean;
}

/** Marketing primary links — shown to LOGGED-OUT visitors. */
const MARKETING_LINKS: PrimaryLink[] = [
  {
    href: "/compass",
    label: "Compass",
    isActive: (p) => p === "/compass" || p.startsWith("/compass/"),
  },
  {
    // Marketing page about the stock-report product. The app's report
    // browser stays at /browse (linked from the logged-in tabs below
    // and the mobile Explore group).
    href: "/reports",
    label: "Reports",
    isActive: (p) => p === "/reports" || p.startsWith("/reports/"),
  },
  {
    // The third product's marketing page (anonymous → SimulateProductPage).
    href: "/simulate",
    label: "Simulate",
    isActive: (p) => p === "/simulate" || p.startsWith("/simulate/"),
  },
  {
    href: "/methodology",
    label: "Methodology",
    isActive: (p) => p === "/methodology",
  },
  {
    href: "/blog",
    label: "Blog",
    isActive: (p) => p === "/blog" || p.startsWith("/blog/"),
  },
  {
    href: "/pricing",
    label: "Pricing",
    isActive: (p) => p === "/pricing",
  },
];

/**
 * App primary links — shown to LOGGED-IN users. This is the old
 * `Navbar` tab set (Stocks / Funds / Compass / Simulate / Industry /
 * Glossary / Blog) restored after the Phase 3 shell unification
 * dropped it, plus Dashboard. Active-route detection matches the old
 * Navbar's per-tab logic.
 */
const APP_LINKS: PrimaryLink[] = [
  {
    href: "/dashboard",
    label: "Dashboard",
    isActive: (p) => p === "/dashboard",
  },
  {
    href: "/browse",
    label: "Stocks",
    isActive: (p) =>
      p === "/browse" ||
      p === "/search" ||
      p === "/my-reports" ||
      p.startsWith("/report/") ||
      p.startsWith("/stocks/"),
  },
  {
    href: "/funds",
    label: "Funds",
    isActive: (p) => p === "/funds" || p.startsWith("/funds/"),
  },
  {
    href: "/compass",
    label: "Compass",
    isActive: (p) => p === "/compass" || p.startsWith("/compass/"),
  },
  {
    href: "/simulate",
    label: "Simulate",
    isActive: (p) => p === "/simulate" || p.startsWith("/simulate/"),
  },
  {
    href: "/industry",
    label: "Industry",
    isActive: (p) => p === "/industry" || p.startsWith("/industry/"),
  },
  {
    href: "/glossary",
    label: "Glossary",
    isActive: (p) => p === "/glossary",
  },
  {
    href: "/blog",
    label: "Blog",
    isActive: (p) => p === "/blog" || p.startsWith("/blog/"),
  },
];

/**
 * Secondary destinations for the LOGGED-OUT mobile overlay — every app
 * route the marketing primary set doesn't cover. (Logged-in users get
 * all of these in their primary tabs, so their mobile overlay shows a
 * "More" group with the marketing pages instead.)
 */
const EXPLORE_LINKS = [
  { href: "/browse", label: "Stocks" },
  { href: "/funds", label: "Funds" },
  { href: "/simulate", label: "Simulate" },
  { href: "/industry", label: "Industry" },
  { href: "/glossary", label: "Glossary" },
  { href: "/blog", label: "Blog" },
];

/**
 * Secondary destinations for the LOGGED-IN mobile overlay — marketing
 * pages not in the app tab set (kept reachable so nothing previously
 * in the unified mobile menu is dropped for signed-in users).
 */
const MORE_LINKS = [
  { href: "/compass#sample-briefs", label: "Sample brief" },
  { href: "/methodology", label: "Methodology" },
  { href: "/pricing", label: "Pricing" },
];

export function SiteNav({ topOffset = 0 }: SiteNavProps) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const loggedIn = status === "authenticated";
  const [accountOpen, setAccountOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [unreadFeedback, setUnreadFeedback] = useState(0);

  // Existing session-side-channel logic carried over from the old
  // Navbar verbatim: plan badge + admin detection with unread count.
  useEffect(() => {
    if (!loggedIn) return;
    (async () => {
      try {
        const res = await fetch("/api/user/plan");
        const json = await res.json();
        if (json.success) setUserPlan(json.plan);
      } catch {
        /* ignore */
      }
    })();
    (async () => {
      try {
        const res = await fetch("/api/admin/stats");
        if (res.ok) {
          const json = await res.json();
          if (json.success) {
            setIsAdmin(true);
            setUnreadFeedback(json.data.unreadFeedback);
          }
        }
      } catch {
        /* ignore */
      }
    })();
  }, [loggedIn]);

  // Close any open menu on route change (backstop — links also close
  // on click so the overlay never lingers after navigation).
  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
  }, [pathname]);

  // Body scroll-lock while the mobile overlay is open (a11y carry-over
  // from the marketing nav).
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  // Escape closes whichever menu is open.
  useEffect(() => {
    if (!mobileOpen && !accountOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setAccountOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, accountOpen]);

  // Auth-aware primary set: app tabs for signed-in users, marketing
  // links for visitors. (While the session is still loading we show the
  // marketing set — same first-paint behaviour as the old shells.)
  const primaryLinks = loggedIn ? APP_LINKS : MARKETING_LINKS;

  const navTabClass = (active: boolean) =>
    `text-[13px] font-semibold px-3.5 py-1.5 rounded-lg transition no-underline ${
      active
        ? "bg-blue/15 text-blue"
        : "text-muted-foreground hover:text-foreground hover:bg-surface-light/20"
    }`;

  return (
    <>
      <header
        className="sticky z-50 flex items-center justify-between px-6 py-3 bg-navy/95 border-b border-surface-light/30 backdrop-blur-xl"
        style={{ top: topOffset }}
      >
        <Logo href={loggedIn ? "/dashboard" : "/"} />

        <div className="flex items-center gap-3">
          {/* Desktop primary links — app tabs when logged in,
              marketing links when logged out */}
          <nav
            aria-label="Primary"
            className="hidden md:flex items-center gap-1"
          >
            {primaryLinks.map((link) => (
              <Link
                key={link.href + link.label}
                href={link.href}
                className={navTabClass(link.isActive(pathname))}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth-aware right side */}
          {loggedIn && session?.user ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setAccountOpen(!accountOpen)}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                className="flex items-center gap-2 cursor-pointer bg-transparent border-none"
              >
                {session.user.image ? (
                  <Image
                    src={session.user.image}
                    alt={session.user.name ?? "User"}
                    width={32}
                    height={32}
                    className="rounded-full border-2 border-blue/30"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cgreen to-blue flex items-center justify-center text-white font-bold text-sm">
                    {(session.user.name ?? "U")[0].toUpperCase()}
                  </div>
                )}
                <span className="text-foreground text-sm font-medium hidden sm:block">
                  {session.user.name?.split(" ")[0]}
                </span>
                {(userPlan === "PRO" || userPlan === "PREMIUM") && (
                  <span className="bg-blue/20 text-blue px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide hidden sm:block">
                    PRO
                  </span>
                )}
              </button>
              {accountOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setAccountOpen(false)}
                    aria-hidden="true"
                  />
                  <div
                    role="menu"
                    aria-label="Account"
                    className="absolute right-0 top-full mt-2 w-48 bg-surface border border-surface-light/30 rounded-lg shadow-xl z-50 overflow-hidden"
                  >
                    <Link
                      href="/dashboard"
                      role="menuitem"
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-navy-mid no-underline"
                      onClick={() => setAccountOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link
                      href="/my-reports"
                      role="menuitem"
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-navy-mid no-underline"
                      onClick={() => setAccountOpen(false)}
                    >
                      My Reports
                    </Link>
                    <Link
                      href="/browse"
                      role="menuitem"
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-navy-mid no-underline"
                      onClick={() => setAccountOpen(false)}
                    >
                      Browse Reports
                    </Link>
                    <Link
                      href="/account"
                      role="menuitem"
                      className="block px-4 py-2.5 text-sm text-foreground hover:bg-navy-mid no-underline"
                      onClick={() => setAccountOpen(false)}
                    >
                      Account
                    </Link>
                    {isAdmin && (
                      <Link
                        href="/admin"
                        role="menuitem"
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gold hover:bg-navy-mid no-underline"
                        onClick={() => setAccountOpen(false)}
                      >
                        Admin
                        {unreadFeedback > 0 && (
                          <span className="bg-cred text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                            {unreadFeedback}
                          </span>
                        )}
                      </Link>
                    )}
                    <div className="border-t border-surface-light/30" />
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => signOut({ callbackUrl: "/" })}
                      className="block w-full text-left px-4 py-2.5 text-sm text-cred hover:bg-navy-mid cursor-pointer bg-transparent border-none"
                    >
                      Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Button variant="ghost" size="sm" asChild>
                <Link href="/login">Log in</Link>
              </Button>
              <Button size="sm" asChild>
                <Link href="/signup">Start free</Link>
              </Button>
            </div>
          )}

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            aria-controls="site-nav-mobile-menu"
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg border border-surface-light/30 text-muted-foreground hover:text-foreground cursor-pointer bg-transparent"
          >
            <Menu size={20} aria-hidden="true" />
          </button>
        </div>
      </header>

      {/* Mobile full-screen overlay (z-70 sits above the 32px marketing
          TickerBar at z-60) */}
      {mobileOpen && (
        <div
          id="site-nav-mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-xl flex flex-col md:hidden"
        >
          <div className="flex items-center justify-between px-6 py-3 border-b border-surface-light/30">
            <Logo href={loggedIn ? "/dashboard" : "/"} />
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-surface-light/30 text-muted-foreground hover:text-foreground cursor-pointer bg-transparent"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <nav
            aria-label="Site navigation"
            className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-1"
          >
            {primaryLinks.map((link) => (
              <Link
                key={link.href + link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={`py-3 px-2 text-lg font-semibold border-b border-surface-light/20 no-underline ${
                  link.isActive(pathname) ? "text-blue" : "text-foreground"
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="text-dim text-[10px] uppercase tracking-widest mt-6 mb-1 px-2">
              {loggedIn ? "More" : "Explore"}
            </div>
            {(loggedIn ? MORE_LINKS : EXPLORE_LINKS).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="py-2.5 px-2 text-[15px] text-muted-foreground hover:text-foreground no-underline"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="px-6 py-5 border-t border-surface-light/30 flex flex-col gap-2.5">
            {loggedIn ? (
              <>
                <Button variant="secondary" asChild>
                  <Link href="/dashboard" onClick={() => setMobileOpen(false)}>
                    Dashboard
                  </Link>
                </Button>
                <Button asChild>
                  <Link href="/my-reports" onClick={() => setMobileOpen(false)}>
                    My Reports
                  </Link>
                </Button>
                <button
                  type="button"
                  onClick={() => signOut({ callbackUrl: "/" })}
                  className="py-2.5 text-sm text-cred text-center cursor-pointer bg-transparent border-none"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Button variant="secondary" asChild>
                  <Link href="/login" onClick={() => setMobileOpen(false)}>
                    Log in
                  </Link>
                </Button>
                <Button asChild>
                  <Link href="/signup" onClick={() => setMobileOpen(false)}>
                    Start free
                  </Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
